/**
 * =============================================================================
 * Hook: useSearchTenantEntities
 * Versão: 1.0.0 (2026-04-30)
 *
 * Descrição:
 *   Wrapper genérico para a RPC `search_tenant_entities`. Permite buscar
 *   server-side por Clientes (`crm_customers`) e Vendedores (`crm_sellers`)
 *   do tenant ativo, evitando o limite de 1.000 linhas do PostgREST.
 *
 *   Usado pelos comboboxes de filtro avançado da página de Comissões e
 *   reutilizável em qualquer outro picker similar do app.
 *
 *   - Debounce externo (passe `query` já estabilizado pelo caller).
 *   - Default `limit = 20`.
 * =============================================================================
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('SearchTenantEntities');

/** Tipos de entidade suportados pela RPC. */
export type TenantSearchEntity = 'customer' | 'seller';

/** Item de resultado padronizado para uso em comboboxes. */
export interface TenantEntityItem {
  id: string;
  label: string;
  hint: string;
}

interface UseSearchTenantEntitiesOptions {
  entity: TenantSearchEntity;
  /** Texto de busca já com debounce aplicado pelo componente caller. */
  query: string;
  /** Tamanho máximo da página (default 20, máx 50 no backend). */
  limit?: number;
  /** Habilita/desabilita a query (ex.: enquanto popover está fechado). */
  enabled?: boolean;
}

/**
 * Hook principal — search server-side de entidades do tenant.
 */
export function useSearchTenantEntities({
  entity,
  query,
  limit = 20,
  enabled = true,
}: UseSearchTenantEntitiesOptions) {
  const { currentTenant } = useAuth();

  return useQuery({
    queryKey: [
      'search-tenant-entities',
      currentTenant?.id,
      entity,
      query,
      limit,
    ],
    queryFn: async (): Promise<TenantEntityItem[]> => {
      logger.debug('Buscando entidades do tenant', { entity, query, limit });

      // Casting necessário até `types.ts` ser regenerado
      const { data, error } = await (supabase.rpc as any)(
        'search_tenant_entities',
        {
          p_entity: entity,
          p_query: query || null,
          p_limit: limit,
          p_tenant_hint: currentTenant?.id || null,
        },
      );

      if (error) {
        logger.error('Erro ao buscar entidades do tenant', error);
        throw error;
      }

      return (data || []) as TenantEntityItem[];
    },
    enabled: enabled && !!currentTenant?.id,
    staleTime: 30 * 1000,
    gcTime: 60 * 1000,
  });
}
